import { Fragment, type DragEvent } from "react";
import { ArrowDown, ArrowUp, GripVertical } from "lucide-react";
import { Button } from "@/shared/components/shadcn/Button";
import { cn } from "@/shared/utils/cn";
import type { BuilderDraft, BuilderElement } from "@froppio/form-core";
import type { BuilderDragSource, BuilderDropTarget } from "../types/builderDragTypes";
import { elementId } from "../utils/builderOperations";
import { widgetLabel } from "@froppio/form-core";
import { BuilderDropZone } from "./BuilderDropZone";

interface BuilderCanvasProps {
  draft: BuilderDraft;
  selectedId: string | null;
  dragSource: BuilderDragSource | null;
  onSelect: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onDragStart: (event: DragEvent<HTMLElement>, source: BuilderDragSource) => void;
  onDragEnd: () => void;
  onDrop: (target: BuilderDropTarget) => void;
}

export function BuilderCanvas({ draft, selectedId, dragSource, onSelect, onMove, onDragStart, onDragEnd, onDrop }: BuilderCanvasProps) {
  const draggingSection = dragSource?.type === "palette" ? dragSource.widget === "section" : dragSource?.isSection;

  function dropZone(sectionId: string | null, index: number) {
    return <BuilderDropZone target={{ sectionId, index }} active={!!dragSource && (!sectionId || !draggingSection)}
      label={`Insert at position ${index + 1}${sectionId ? " in section" : " in form"}`} onDrop={onDrop} />;
  }

  function renderElement(element: BuilderElement, index: number, count: number) {
    const id = elementId(element);
    const field = element.type === "field" ? draft.fields[id] : undefined;
    const label = element.type === "section" ? element.title || "Untitled section" : field!.label || "Untitled question";
    const description = element.type === "section" ? element.description : field!.helpText;
    return (
      <article data-builder-element={id} aria-label={label}
        className={cn("min-w-0 rounded-[20px] border bg-card p-4",
          selectedId === id ? "border-primary ring-2 ring-ring" : "border-border")}>
        <div className="flex items-start gap-1">
          <Button variant="ghost" size="icon-sm" draggable aria-label={`Drag ${label}`}
            onDragStart={event => onDragStart(event, { type: "element", id, isSection: element.type === "section" })}
            onDragEnd={onDragEnd} onClick={() => onSelect(id)} className="shrink-0 cursor-grab active:cursor-grabbing">
            <GripVertical aria-hidden="true" />
          </Button>
          <button type="button" onClick={() => onSelect(id)} aria-pressed={selectedId === id}
            aria-label={`Edit ${label}`} className="min-h-11 min-w-0 flex-1 rounded-xl px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <span className="block break-words text-sm font-bold">{label}{field?.required ? " *" : ""}</span>
            <span className="block text-xs text-muted-foreground">{field ? widgetLabel(field.widget) : "Section"}</span>
          </button>
          <div className="flex shrink-0">
            <Button variant="ghost" size="icon-sm" aria-label={`Move ${label} up`} disabled={index === 0} onClick={() => onMove(id, -1)}><ArrowUp aria-hidden="true" /></Button>
            <Button variant="ghost" size="icon-sm" aria-label={`Move ${label} down`} disabled={index === count - 1} onClick={() => onMove(id, 1)}><ArrowDown aria-hidden="true" /></Button>
          </div>
        </div>
        {description && <p className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">{description}</p>}
        {field && (
          <div aria-hidden="true" className="mt-3 rounded-xl border border-dashed border-input bg-background px-3 py-3 text-sm text-muted-foreground">
            {field.choices.length ? field.choices.map(choice => choice.label || "Untitled option").join(" / ")
              : field.widget === "signature" ? "Draw signature here"
                : field.widget === "yesNo" ? "Yes / No" : "Answer"}
          </div>
        )}
        {element.type === "section" && (
          <div className="mt-2 border-l-2 border-border pl-3">
            {dropZone(element.id, 0)}
            {!element.elements.length && <p className="py-3 text-sm text-muted-foreground">Select this section and add a question, or drag one here.</p>}
            {element.elements.map((child, childIndex) => (
              <Fragment key={child.fieldId}>
                {renderElement(child, childIndex, element.elements.length)}
                {dropZone(element.id, childIndex + 1)}
              </Fragment>
            ))}
          </div>
        )}
      </article>
    );
  }

  return (
    <section aria-label="Form canvas" className="min-w-0">
      <h2 className="text-base font-bold">Your form</h2>
      <p className="mt-1 text-xs text-muted-foreground">Select an item to edit it. Use arrows or settings to move it without dragging.</p>
      {dropZone(null, 0)}
      {!draft.elements.length && <div className="rounded-[20px] border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Start with a question or section from the palette.</div>}
      {draft.elements.map((element, index) => (
        <Fragment key={elementId(element)}>
          {renderElement(element, index, draft.elements.length)}
          {dropZone(null, index + 1)}
        </Fragment>
      ))}
    </section>
  );
}
